/**
 * Per-cooperative module assignment editor (platform admin).
 * Replaces Investment Hub capability toggles — modules are the source of truth.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FaCheck, FaCubes, FaTimes } from "react-icons/fa";
import { ModalCompAdvance } from "../Ui/ModalComp";
import Message from "../MessageModal";
import {
  useGetCooperativeModuleAssignmentsQuery,
  useUpdateCooperativeModuleAssignmentsMutation,
  useResetCooperativeModuleAssignmentsMutation,
} from "../../store/auth/authApi";

function StatusPill({ tone = "neutral", children }) {
  const tones = {
    success: "bg-green-50 text-green-700 border-green-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    info: "bg-cyan-50 text-[#31778c] border-cyan-200",
    neutral: "bg-gray-50 text-gray-600 border-gray-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

function sourceLabel(row) {
  if (row.replaces_module_name) {
    return { text: `Replaces ${row.replaces_module_name}`, tone: "warning" };
  }
  if (row.source === "manager_override") {
    return { text: "Override", tone: "warning" };
  }
  if (row.on_plan && row.is_enabled) {
    return { text: "Plan default", tone: "info" };
  }
  if (row.on_plan && !row.is_enabled) {
    return { text: "On plan (disabled)", tone: "neutral" };
  }
  if (!row.on_plan && row.is_enabled) {
    return { text: "Out of plan", tone: "warning" };
  }
  return { text: "Not assigned", tone: "neutral" };
}

function buildDraftFromSnapshot(snapshot) {
  const modules = snapshot?.effective_modules || [];
  return modules.map((row) => ({
    module_id: row.module_id,
    is_enabled: Boolean(row.is_enabled),
    sub_module_ids: [...(row.enabled_sub_module_ids || [])],
    replaces_module_id: row.replaces_module_id || null,
    notes: row.notes || "",
    _meta: row,
  }));
}

export default function CooperativeModuleAssignmentCard({ companyId }) {
  const { data, isLoading, isFetching, refetch } = useGetCooperativeModuleAssignmentsQuery(companyId, {
    skip: !companyId,
  });
  const [updateAssignments, { isLoading: isSaving }] = useUpdateCooperativeModuleAssignmentsMutation();
  const [resetAssignments, { isLoading: isResetting }] = useResetCooperativeModuleAssignmentsMutation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [draft, setDraft] = useState([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const snapshot = data || null;
  const planBaseline = snapshot?.plan_baseline?.modules || [];
  const effectiveModules = snapshot?.effective_modules || [];
  const capabilities = snapshot?.capabilities?.effective || {};

  useEffect(() => {
    if (!isEditMode && snapshot) {
      setDraft(buildDraftFromSnapshot(snapshot));
    }
  }, [snapshot, isEditMode]);

  const isDirty = useMemo(() => {
    const original = buildDraftFromSnapshot(snapshot || {});
    if (draft.length !== original.length) return true;
    return draft.some((row, idx) => {
      const base = original[idx];
      if (!base || row.module_id !== base.module_id) return true;
      if (Boolean(row.is_enabled) !== Boolean(base.is_enabled)) return true;
      const a = [...(row.sub_module_ids || [])].sort().join(",");
      const b = [...(base.sub_module_ids || [])].sort().join(",");
      return a !== b;
    });
  }, [draft, snapshot]);

  const startEdit = () => {
    setDraft(buildDraftFromSnapshot(snapshot || {}));
    setIsEditMode(true);
  };

  const cancelEdit = () => {
    setDraft(buildDraftFromSnapshot(snapshot || {}));
    setIsEditMode(false);
  };

  const toggleModule = (moduleId, enabled) => {
    setDraft((prev) =>
      prev.map((row) => (row.module_id === moduleId ? { ...row, is_enabled: enabled } : row))
    );
  };

  const toggleSubModule = (moduleId, subId) => {
    setDraft((prev) =>
      prev.map((row) => {
        if (row.module_id !== moduleId) return row;
        const subs = new Set(row.sub_module_ids || []);
        if (subs.has(subId)) subs.delete(subId);
        else subs.add(subId);
        return { ...row, sub_module_ids: [...subs], is_enabled: subs.size > 0 || row.is_enabled };
      })
    );
  };

  const performSave = async () => {
    const payload = {
      companyId,
      modules: draft.map(({ module_id, is_enabled, sub_module_ids, replaces_module_id, notes }) => ({
        module_id,
        is_enabled,
        sub_module_ids,
        replaces_module_id,
        notes: notes || null,
      })),
    };

    try {
      await updateAssignments(payload).unwrap();
      toast.success("Module assignments updated.");
      setIsEditMode(false);
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || error?.error || "Unable to update module assignments.");
    }
  };

  const performReset = async () => {
    try {
      await resetAssignments(companyId).unwrap();
      toast.success("Module assignments reset to plan defaults.");
      setShowResetConfirm(false);
      setIsEditMode(false);
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || error?.error || "Unable to reset module assignments.");
    }
  };

  if (isLoading) {
    return (
      <div className="md:col-span-2 mt-6 pt-6 border-t" aria-busy="true" aria-label="Loading module assignments">
        <div className="animate-pulse space-y-3">
          <div className="h-5 w-48 bg-gray-200 rounded" />
          <div className="h-32 bg-gray-100 rounded" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="md:col-span-2 space-y-4 mt-6 pt-6 border-t">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h4 className="text-md font-semibold text-gray-700 mb-1 flex items-center">
            <FaCubes className="mr-2 text-[#8B313A]" />
            Module assignments
          </h4>
          <p className="text-sm text-gray-500 max-w-2xl">
            Control which platform modules this cooperative may use. Overrides persist across plan changes until reset.
            Member/investor capabilities are derived from enabled modules.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isEditMode ? (
            <>
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center rounded-md border border-[#8B313A] px-3 py-1.5 text-sm font-medium text-[#8B313A] hover:bg-[#8B313A]/5"
              >
                Edit modules
              </button>
              <button
                type="button"
                onClick={() => setShowResetConfirm(true)}
                disabled={isResetting}
                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Reset to plan
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={isSaving}
                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <FaTimes className="mr-1.5" /> Cancel
              </button>
              <button
                type="button"
                onClick={performSave}
                disabled={isSaving || !isDirty}
                className="inline-flex items-center rounded-md bg-[#8B313A] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                <FaCheck className="mr-1.5" />
                {isSaving ? "Saving…" : "Save changes"}
              </button>
            </>
          )}
        </div>
      </div>

      {planBaseline.length > 0 ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span className="font-medium">Plan baseline:</span>{" "}
          {planBaseline.map((m) => m.name).join(", ")}
        </div>
      ) : null}

      {isFetching && !isLoading ? (
        <p className="text-xs text-gray-500">Refreshing…</p>
      ) : null}

      <div className="grid gap-3">
        {(isEditMode ? draft : effectiveModules.map((row) => ({ ...row, _meta: row }))).map((row) => {
          const meta = row._meta || row;
          const moduleRow = isEditMode
            ? meta
            : effectiveModules.find((m) => m.module_id === row.module_id) || row;
          const status = sourceLabel(moduleRow);
          const enabled = isEditMode ? Boolean(row.is_enabled) : Boolean(moduleRow.is_enabled);
          const subModules = moduleRow.sub_modules || [];

          return (
            <div
              key={moduleRow.module_id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-800">{moduleRow.name}</span>
                    <StatusPill tone={status.tone}>{status.text}</StatusPill>
                    {enabled ? (
                      <StatusPill tone="success">Enabled</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Disabled</StatusPill>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{moduleRow.url}</p>
                </div>
                {isEditMode ? (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => toggleModule(moduleRow.module_id, e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Enable module
                  </label>
                ) : null}
              </div>

              {subModules.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {subModules.map((sub) => {
                    const subEnabled = isEditMode
                      ? (row.sub_module_ids || []).includes(sub.id)
                      : (moduleRow.enabled_sub_module_ids || []).includes(sub.id);
                    return (
                      <label
                        key={sub.id}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                          subEnabled
                            ? "border-[#31778c] bg-[#eaf1f4] text-[#31778c]"
                            : "border-gray-200 bg-gray-50 text-gray-500"
                        }`}
                      >
                        {isEditMode ? (
                          <input
                            type="checkbox"
                            checked={subEnabled}
                            disabled={!enabled}
                            onChange={() => toggleSubModule(moduleRow.module_id, sub.id)}
                            className="rounded border-gray-300"
                          />
                        ) : null}
                        {sub.name}
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {capabilities && Object.keys(capabilities).length > 0 ? (
        <div className="rounded-md border border-cyan-100 bg-cyan-50/50 px-3 py-2 text-xs text-gray-600">
          <span className="font-medium text-gray-700">Effective capabilities: </span>
          {Object.entries(capabilities)
            .filter(([, v]) => v)
            .map(([k]) => k.replace(/_enabled$/, ""))
            .join(", ") || "none"}
        </div>
      ) : null}

      <ModalCompAdvance
        show={showResetConfirm}
        handleClose={() => setShowResetConfirm(false)}
        centered
        size="md"
      >
        <Message
          title="Reset to plan defaults?"
          desc="This removes all manager overrides and re-seeds modules from the current subscription plan. Staff role modules will be re-synced."
          firstBtnText={isResetting ? "Resetting…" : "Reset"}
          secondBtnText="Cancel"
          firstBtnClass="bg-[#8B313A] text-white"
          onFirstBtnClick={performReset}
          onSecondBtnClick={() => setShowResetConfirm(false)}
          loading={isResetting}
        />
      </ModalCompAdvance>
    </div>
  );
}
